import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, IconButton, Typography, TablePagination, TextField, useTheme, useMediaQuery, CardContent, Stack, InputAdornment, Tooltip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon, CloudUpload as CloudUploadIcon, CloudDownload as CloudDownloadIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import movMonedaService, { MovMoneda as MovMonedaItem } from '../../services/movMonedaService';
import * as XLSX from 'xlsx';

const formatFechaDisplay = (fecha: string): string => {
  if (!fecha) return '-';
  const d = new Date(fecha.includes('T') ? fecha : `${fecha}T12:00:00`);
  return Number.isFinite(d.getTime()) ? d.toLocaleDateString('es-AR') : fecha;
};

const MovMoneda: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [movimientos, setMovimientos] = useState<MovMonedaItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [_loading, setLoading] = useState(false);

  const fetchMovMonedas = async (pageNum: number = page, pageSize: number = rowsPerPage, search: string = searchTerm) => {
    try {
      setLoading(true);
      const data = await movMonedaService.getMovMonedasPaged({
        pageNumber: pageNum + 1,
        pageSize: pageSize,
        searchTerm: search || undefined,
      });
      setMovimientos(data.items);
      setTotalCount(data.totalCount);
    } catch (error: unknown) {
      console.error('Error al cargar cotizaciones de moneda:', error);
      enqueueSnackbar('Error al cargar cotizaciones de moneda', { variant: 'error' });
      setMovimientos([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMovMonedas();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(0);
      fetchMovMonedas(0, rowsPerPage, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    fetchMovMonedas(newPage, rowsPerPage, searchTerm);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchMovMonedas(0, newRowsPerPage, searchTerm);
  };

  const handleDelete = async (mov: MovMonedaItem) => {
    if (!window.confirm('¿Está seguro de eliminar esta cotización de moneda?')) return;
    try {
      await movMonedaService.deleteMovMoneda(mov.codigoMoneda, mov.fechaCotizacion);
      enqueueSnackbar('Cotización eliminada exitosamente', { variant: 'success' });
      fetchMovMonedas(page, rowsPerPage, searchTerm);
    } catch (error: unknown) {
      console.error('Error al eliminar cotización:', error);
      const err = error as { response?: { data?: string | { message?: string } } };
      const msg = typeof err?.response?.data === 'string'
        ? err.response.data
        : err?.response?.data?.message || 'Error al eliminar la cotización';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const editPath = (mov: MovMonedaItem) =>
    `/sistema/movmoneda/${mov.codigoMoneda}/${encodeURIComponent(mov.fechaCotizacion)}`;

  const handleExportExcel = async () => {
    try {
      const allMovimientos = await movMonedaService.getAllMovMonedas(searchTerm || undefined);
      const dataToExport = allMovimientos.map((m) => ({
        'Código Moneda': m.codigoMoneda,
        Moneda: m.nombreMoneda || '',
        Fecha: m.fechaCotizacion,
        Cotización: m.cotizacion,
      }));
      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Cotizaciones');
      XLSX.writeFile(wb, 'Cotizaciones_Moneda.xlsx');
      enqueueSnackbar('Archivo exportado exitosamente', { variant: 'success' });
    } catch {
      enqueueSnackbar('Error al exportar datos', { variant: 'error' });
    }
  };

  const handleImportExcel = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        let successCount = 0;
        let errorCount = 0;

        for (const row of jsonData as Record<string, unknown>[]) {
          try {
            const fechaRaw = row['Fecha'] ?? row['fechaCotizacion'];
            let fechaCotizacion = '';
            if (fechaRaw instanceof Date) {
              fechaCotizacion = fechaRaw.toISOString().split('T')[0];
            } else if (typeof fechaRaw === 'number') {
              const excelDate = XLSX.SSF.parse_date_code(fechaRaw);
              if (excelDate) {
                fechaCotizacion = `${excelDate.y}-${String(excelDate.m).padStart(2, '0')}-${String(excelDate.d).padStart(2, '0')}`;
              }
            } else {
              fechaCotizacion = String(fechaRaw ?? '').split('T')[0];
            }

            await movMonedaService.createMovMoneda({
              codigoMoneda: Number(row['Código Moneda'] ?? row['codigoMoneda']),
              fechaCotizacion,
              cotizacion: Number(row['Cotización'] ?? row['cotizacion']),
            });
            successCount++;
          } catch (error) {
            errorCount++;
            console.error('Error al importar cotización:', error);
          }
        }

        enqueueSnackbar(`Importación completada: ${successCount} exitosos, ${errorCount} errores`, {
          variant: errorCount > 0 ? 'warning' : 'success',
        });
        fetchMovMonedas(page, rowsPerPage, searchTerm);
      } catch {
        enqueueSnackbar('Error al procesar el archivo Excel', { variant: 'error' });
      }
    };
    reader.readAsArrayBuffer(file);
    event.target.value = '';
  };

  const pagination = (
    <TablePagination
      component="div"
      count={totalCount}
      page={page}
      onPageChange={handleChangePage}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={handleChangeRowsPerPage}
      labelRowsPerPage="Filas por página:"
      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
    />
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Cotizaciones de moneda
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <input
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                id="import-excel-movmoneda"
                type="file"
                onChange={handleImportExcel}
              />
              <label htmlFor="import-excel-movmoneda">
                <Button variant="outlined" component="span" startIcon={<CloudUploadIcon />} size="small">
                  Importar
                </Button>
              </label>
              <Button variant="outlined" startIcon={<CloudDownloadIcon />} onClick={handleExportExcel} size="small">
                Exportar
              </Button>
              <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate('/sistema/movmoneda/nuevo')}>
                Nueva cotización
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por código de moneda..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
            />
          </Box>

          {isMobile ? (
            <Stack spacing={2}>
              {movimientos.map((mov) => (
                <Card key={`${mov.codigoMoneda}-${mov.fechaCotizacion}`} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {mov.nombreMoneda || `Moneda ${mov.codigoMoneda}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Cotización: {mov.cotizacion?.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Fecha: {formatFechaDisplay(mov.fechaCotizacion)}
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <IconButton size="small" onClick={() => navigate(editPath(mov))} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(mov)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Card>
              ))}
              {pagination}
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Moneda</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Cotización</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {movimientos.map((mov) => (
                    <TableRow key={`${mov.codigoMoneda}-${mov.fechaCotizacion}`} hover>
                      <TableCell>{mov.codigoMoneda}</TableCell>
                      <TableCell>{mov.nombreMoneda || '-'}</TableCell>
                      <TableCell>{formatFechaDisplay(mov.fechaCotizacion)}</TableCell>
                      <TableCell>
                        {mov.cotizacion?.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => navigate(editPath(mov))} color="primary">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" onClick={() => handleDelete(mov)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {pagination}
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default MovMoneda;
